import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Flex } from '@chakra-ui/react';

import {
  Feature,
  FeatureFilterField,
  FeatureName,
  TeamMembersRequestStatus,
  UserFilterValues,
} from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanyProfile } from 'common/hooks/useCompanyProfile';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import { useHasSeenNewFeature } from 'common/hooks/useHasSeenNewFeature';
import useTeamRequestsCreatedSubscription from 'common/hooks/useTeamRequestsCreatedSubscription';
import * as accountingRoutes from 'features/Accounting/routes';
import * as bankRoutes from 'features/Bank/routes';
import * as bankTransferRequestsRoutes from 'features/BankTransferRequests/routes';
import * as dashboardRoutes from 'features/Dashboard/routes';
import * as expenseClaimRequestsRoutes from 'features/ExpenseClaimRequests/routes';
import * as helpRoutes from 'features/Help/routes';
import * as insuranceRoutes from 'features/Insurance/routes';
import * as paymentCardsRoutes from 'features/PaymentCards/routes';
import paymentRoutes from 'features/Payments/routes';
import useTeamMembersInvitationQuota from 'features/Team/hooks/useTeamMembersInvitationQuota';
import { useRequests } from 'features/Team/pages/Requests/hooks/useRequests';
import * as teamRoutes from 'features/Team/routes';
import * as toolboxRoutes from 'features/Toolbox/routes';
import * as upgradeRoutes from 'features/Upgrade/routes';
import * as workRoutes from 'features/Work/routes';

import { NavMenuTeamRequestsDocument } from '../hooks/useTeamRequests';
import { Indicator } from './Indicator';
import { locales } from './locales';
import { NavigationCategory } from './NavigationCategory';
import { NavigationEntry } from './NavigationEntry';
import { NavigationSection } from './NavigationSection';

export function Navigation() {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const location = useLocation();
  const { featureFlags } = useFeatureFlagsSwitcher();
  const { companyProfileId } = useCompanyContext();
  const { company } = useCompanyProfile(companyProfileId);
  const { isFeatureAvailable, plan } = useCurrentPricingPlan();
  const {
    pageInfo: { totalCount: teamRequestTotalCount },
  } = useRequests([TeamMembersRequestStatus.Pending], {
    fetchPolicy: 'cache-first',
  });
  const { teamMemberInvitationsQuota } = useTeamMembersInvitationQuota({
    skip:
      !isFeatureAvailable(Feature.TeamManagement) ||
      !canAccessFeature({ name: FeatureName.TeamMembers }),
  });
  const isOnTeamRoutes =
    location.pathname.includes(teamRoutes.requests) ||
    location.pathname.includes(teamRoutes.members);

  useTeamRequestsCreatedSubscription({
    onData: ({ client }) => {
      client.refetchQueries({
        include: [NavMenuTeamRequestsDocument],
      });
    },
  });

  const [hasSeenBankAccounts] = useHasSeenNewFeature('BANK_ACCOUNTS');

  const [hasSeenDashboard, setHasSeenDashboard] =
    useHasSeenNewFeature('DASHBOARD');

  const [hasSeenUrssafEstimation, setHasSeenUrssafEstimation] =
    useHasSeenNewFeature('URSSAF_ESTIMATION');

  const hasPendingTeamMembersRequests = teamRequestTotalCount > 0;
  const teamMembersRequestsCount = hasPendingTeamMembersRequests
    ? `(${teamRequestTotalCount})`
    : '';

  /**
   * Here we could have used fragments instead of arrays,
   * but because we are using Children.map in NavigationSection, we can't have a parent wrapping a NavigationEntry otherwise we would not be able to pick up the state of the children.
   */
  const calculatedBankRoutes = useMemo(() => {
    return canAccessFeature({ name: FeatureName.BankInfo }) ||
      canAccessFeature({ name: FeatureName.Bank })
      ? [
          <NavigationEntry
            key={bankRoutes.bankOverview}
            to={bankRoutes.bankOverview}
          >
            {locales.banking.transactions}
          </NavigationEntry>,
          <NavigationEntry
            isHighlighted={!hasSeenBankAccounts}
            key={bankRoutes.bankAccounts}
            to={bankRoutes.bankAccounts}
          >
            {locales.banking.bankAccounts}
          </NavigationEntry>,
          canAccessFeature({
            name: FeatureName.Dashboard,
            withChildFeatureOnly: true,
          }) &&
          isFeatureAvailable(Feature.CashflowManagementDashboard) &&
          featureFlags.enableCashFlowManagement
            ? [
                <NavigationEntry
                  isHighlighted={!hasSeenDashboard}
                  key={dashboardRoutes.overview}
                  onClick={() => {
                    setHasSeenDashboard(true);
                  }}
                  to={dashboardRoutes.overview}
                >
                  {locales.dashboard.routes.overview}
                </NavigationEntry>,
                <NavigationEntry
                  key={dashboardRoutes.treasury}
                  to={dashboardRoutes.treasury}
                >
                  {locales.dashboard.routes.treasury}
                </NavigationEntry>,
              ]
            : null,
        ]
      : null;
  }, [
    canAccessFeature,
    featureFlags.enableCashFlowManagement,
    hasSeenBankAccounts,
    hasSeenDashboard,
    isFeatureAvailable,
    setHasSeenDashboard,
  ]);

  const bankCategoryHeader = useMemo(() => {
    if (canAccessFeature({ name: FeatureName.Bank })) {
      return locales.banking.proBank;
    } else if (canAccessFeature({ name: FeatureName.BankInfo })) {
      return locales.banking.bank;
    } else {
      return locales.banking.transactions;
    }
  }, [canAccessFeature]);

  return (
    <Flex data-testid="main-menu" flexDirection="column" gap="space-16">
      <NavigationCategory
        title={
          <NavigationCategory.Title>
            {locales.categoryTitles.bank}
          </NavigationCategory.Title>
        }
      >
        <NavigationSection
          icon="wallet"
          isVisible={canAccessFeature({
            name: FeatureName.BankTransactions,
            withChildFeatureOnly: true,
          })}
          sectionRoot={bankRoutes.root}
          title={bankCategoryHeader}
        >
          {calculatedBankRoutes}
        </NavigationSection>
        <NavigationSection
          icon="payment"
          isVisible={canAccessFeature({
            name: FeatureName.Payments,
            withChildFeatureOnly: true,
          })}
          sectionRoot={paymentRoutes.root}
          title={locales.payments}
        />
        <NavigationSection
          icon="transfer"
          isVisible={canAccessFeature({
            name: FeatureName.RequestBanktransfers,
            where: {
              or: [
                {
                  field: FeatureFilterField.User,
                  values: [UserFilterValues.Self],
                },
              ],
            },
          })}
          sectionRoot={bankTransferRequestsRoutes.root}
          title={locales.bankTransfers}
        />
        <NavigationSection
          icon="card-recto"
          isVisible={canAccessFeature({
            name: FeatureName.Paymentcards,
            withChildFeatureOnly: true,
          })}
          sectionRoot={paymentCardsRoutes.root}
          title={locales.creditCard}
        />
      </NavigationCategory>
      <NavigationCategory
        title={
          <NavigationCategory.Title>
            {locales.categoryTitles.management}
          </NavigationCategory.Title>
        }
      >
        <NavigationSection
          icon="receipt"
          isVisible={canAccessFeature({
            name: FeatureName.RequestExpenseclaims,
            where: {
              or: [
                {
                  field: FeatureFilterField.User,
                  values: [UserFilterValues.Self],
                },
              ],
            },
          })}
          sectionRoot={expenseClaimRequestsRoutes.root}
          title={locales.expenseClaims}
        />
        <NavigationSection
          icon="avatar-list"
          isVisible={canAccessFeature({
            name: FeatureName.Invoicing,
            withChildFeatureOnly: true,
          })}
          sectionRoot={workRoutes.root}
          title={locales.work}
        >
          <NavigationEntry to={workRoutes.invoices}>
            {locales.invoices}
          </NavigationEntry>
          <NavigationEntry to={workRoutes.quotes}>
            {locales.quotes}
          </NavigationEntry>
          <NavigationEntry to={workRoutes.clients}>
            {locales.clients}
          </NavigationEntry>
          <NavigationEntry to={workRoutes.catalog}>
            {locales.catalog}
          </NavigationEntry>
        </NavigationSection>
        <NavigationSection
          icon="check"
          isVisible={canAccessFeature({
            name: FeatureName.Accountingexport,
            withChildFeatureOnly: true,
          })}
          onClick={() => setHasSeenUrssafEstimation(true)}
          sectionRoot={accountingRoutes.root}
          title={locales.accounting.main}
        >
          {canAccessFeature({
            name: FeatureName.BankTaxforecasts,
            withChildFeatureOnly: true,
          }) && company?.isMicro ? (
            <NavigationEntry
              isHighlighted={!hasSeenUrssafEstimation}
              onClick={() => {
                setHasSeenUrssafEstimation(true);
              }}
              to={accountingRoutes.estimation}
            >
              {locales.accounting.routes.estimation}
            </NavigationEntry>
          ) : null}
          <NavigationEntry to={accountingRoutes.exports}>
            {locales.accounting.routes.exports}
          </NavigationEntry>
        </NavigationSection>
        <NavigationSection
          icon="user-plus"
          isVisible={
            isFeatureAvailable(Feature.TeamManagement) &&
            canAccessFeature({
              name: FeatureName.Team,
              withChildFeatureOnly: true,
            })
          }
          sectionRoot={teamRoutes.root}
          title={
            <>
              {locales.team.main}
              {hasPendingTeamMembersRequests && !isOnTeamRoutes ? (
                <Indicator />
              ) : null}
            </>
          }
        >
          <NavigationEntry to={teamRoutes.members}>
            {locales.team.routes.members}
          </NavigationEntry>
          {teamMemberInvitationsQuota?.limit !== 1 ? (
            <NavigationEntry to={teamRoutes.requests}>
              <>
                {locales.formatString(locales.team.routes.requests, {
                  count: teamMembersRequestsCount,
                })}
                {hasPendingTeamMembersRequests ? <Indicator /> : null}
              </>
            </NavigationEntry>
          ) : null}
        </NavigationSection>

        <NavigationSection
          icon="cash"
          isVisible={
            featureFlags.enableNewPsp &&
            canAccessFeature({ name: FeatureName.MerchantRead })
          }
          sectionRoot="/psp"
          title={locales.newPsp}
        />
      </NavigationCategory>
      <NavigationCategory
        title={
          <NavigationCategory.Title>
            {locales.categoryTitles.plus}
          </NavigationCategory.Title>
        }
      >
        <NavigationSection
          icon="insurance"
          isVisible={canAccessFeature({
            name: FeatureName.Insurance,
            withChildFeatureOnly: true,
          })}
          sectionRoot={insuranceRoutes.root}
          title={locales.insurances}
        ></NavigationSection>
        <NavigationSection
          icon="toolbox"
          isVisible={canAccessFeature({
            name: FeatureName.Toolbox,
            withChildFeatureOnly: true,
          })}
          sectionRoot={toolboxRoutes.root}
          title={locales.toolbox}
        >
          <NavigationEntry to={toolboxRoutes.offers}>
            {locales.offers}
          </NavigationEntry>
          <NavigationEntry to={toolboxRoutes.integrations}>
            {locales.integrations}
          </NavigationEntry>
          <NavigationEntry to={toolboxRoutes.collections}>
            {locales.collections}
          </NavigationEntry>
        </NavigationSection>
        <NavigationSection
          icon="star"
          isVisible={
            Boolean(plan?.upgradablePlanIds?.length) &&
            canAccessFeature({ name: FeatureName.CompanyPlan })
          }
          sectionRoot={upgradeRoutes.root}
          title={locales.upgradeLabel}
        />
        <NavigationSection
          icon="help"
          sectionRoot={helpRoutes.root}
          title={locales.help}
        />
      </NavigationCategory>
    </Flex>
  );
}
